import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Avatar
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

function Companies() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        phone: '',
        email: '',
        logo: null
    });
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [allCompanies, setAllCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [lastFetchTime, setLastFetchTime] = useState(null);
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
    const user = JSON.parse(localStorage.getItem('user'));
    const isAdmin = user?.role === 'admin';

    const locationTypes = ['Kitchen', 'Market', 'Warehouse', 'Office', 'Other'];

    useEffect(() => {
        const initializeData = async () => {
            try {
                setLoading(true);
                setError(null);
                await fetchCompaniesIfNeeded();
            } catch (err) {
                console.error('Error initializing companies:', err);
                setError('Failed to load companies. Please check your connection and try again.');
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, []);

    const fetchCompaniesIfNeeded = async () => {
        const now = Date.now();
        if (!allCompanies.length || !lastFetchTime || (now - lastFetchTime > CACHE_DURATION)) {
            try {
                const response = await axios.get(`${API_BASE_URL}/companies/read.php`);
                if (response.data && response.data.records) {
                    setAllCompanies(response.data.records);
                    setFilteredCompanies(response.data.records);
                    setLastFetchTime(now);
                } else {
                    throw new Error('Invalid response format from server');
                }
            } catch (err) {
                console.error('Error fetching companies:', err);
                if (err.code === 'ERR_NETWORK') {
                    throw new Error('Network error. Please check your connection and server status.');
                }
                throw err;
            }
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);

        // Client-side filtering
        if (!value.trim()) {
            setFilteredCompanies(allCompanies);
            return;
        }

        const filtered = allCompanies.filter(company => 
            company.name.toLowerCase().includes(value) ||
            (company.location && company.location.toLowerCase().includes(value)) ||
            (company.phone && company.phone.toLowerCase().includes(value)) ||
            (company.description && company.description.toLowerCase().includes(value))
        );

        setFilteredCompanies(filtered);
    };

    const handleOpenDialog = (company = null) => {
        if (company) {
            setIsEditing(true);
            setSelectedCompany(company);
            setFormData({
                name: company.name,
                phone: company.phone || '',
                email: company.email || '',
                description: company.description || '',
                logo: null
            });
            setLogoPreview(company.logo ? `http://localhost:8000/${company.logo}` : null);
        } else {
            setIsEditing(false);
            setSelectedCompany(null);
            setFormData({
                name: '',
                phone: '',
                email: '',
                description: '',
                logo: null
            });
            setLogoPreview(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setIsEditing(false);
        setSelectedCompany(null);
        setFormData({
            name: '',
            phone: '',
            email: '',
            description: '',
            logo: null
        });
        setLogoPreview(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                logo: file
            }));
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const removeLogo = () => {
        setFormData(prev => ({
            ...prev,
            logo: null
        }));
        setLogoPreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess('');

        try {
            let response;
            if (isEditing && selectedCompany) {
                // Update existing company
                const formDataToSend = new FormData();
                formDataToSend.append('id', selectedCompany.id);
                formDataToSend.append('name', formData.name);
                formDataToSend.append('phone', formData.phone);
                formDataToSend.append('email', formData.email || '');
                formDataToSend.append('description', formData.description || '');
                
                // If there's a new logo, include it in the same request
                if (formData.logo) {
                    formDataToSend.append('logo', formData.logo);
                }
                
                // Update company with all data including logo if present
                response = await axios.post(`${API_BASE_URL}/companies/update.php`, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                setSuccess('Company updated successfully');
            } else {
                // Create new company
                const formDataToSend = new FormData();
                formDataToSend.append('name', formData.name);
                formDataToSend.append('phone', formData.phone);
                formDataToSend.append('email', formData.email || '');
                formDataToSend.append('description', formData.description || '');
                
                // If there's a logo, include it in the same request
                if (formData.logo) {
                    formDataToSend.append('logo', formData.logo);
                }
                
                // Create company with all data including logo if present
                response = await axios.post(`${API_BASE_URL}/companies/create.php`, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                setSuccess('Company created successfully');
            }

            handleCloseDialog();
            await fetchCompaniesIfNeeded();
        } catch (err) {
            console.error('Error details:', err.response?.data || err);
            if (err.code === 'ERR_NETWORK') {
                setError('Network error. Please check your connection and try again.');
            } else if (err.code === 'ECONNABORTED') {
                setError('Request timed out. Please try again.');
            } else {
                const errorMessage = err.response?.data?.message || err.message || `Error ${isEditing ? 'updating' : 'creating'} company`;
                setError(errorMessage);
                console.error('Full error response:', err.response?.data);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (companyId) => {
        if (!window.confirm('Are you sure you want to delete this company?')) {
            return;
        }

        try {
            const response = await axios.delete(`${API_BASE_URL}/companies/delete.php`, {
                data: { id: companyId }
            });
            setSuccess('Company deleted successfully');
            await fetchCompaniesIfNeeded();
        } catch (err) {
            console.error('Error deleting company:', err);
            const errorMessage = err.response?.data?.message || 'Error deleting company';
            setError(errorMessage);
        }
    };

    const getLogoUrl = (logoPath) => {
        if (!logoPath) return null;
        // If the path already includes http or https, return as is
        if (logoPath.startsWith('http')) return logoPath;
        // Otherwise, prepend the API base URL
        return `${API_BASE_URL.replace('/api', '')}/${logoPath}`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Companies
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Company
                    </Button>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search companies..."
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                            ),
                        }}
                    />
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {filteredCompanies.length === 0 ? (
                        <Grid item xs={12}>
                            <Typography variant="body1" align="center">
                                {searchTerm ? 'No companies found matching your search' : 'No companies found'}
                            </Typography>
                        </Grid>
                    ) : (
                        filteredCompanies.map((company) => (
                            <Grid item xs={12} sm={6} md={4} key={company.id}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            boxShadow: 6,
                                            cursor: 'pointer'
                                        }
                                    }}
                                    onClick={() => navigate('/zones', { state: { company } })}
                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={getLogoUrl(company.logo)}
                                                    alt={company.name}
                                                    sx={{ width: 50, height: 50 }}
                                                    onError={(e) => {
                                                        e.target.src = ''; // Clear the src on error
                                                        e.target.style.display = 'none'; // Hide the avatar if image fails to load
                                                    }}
                                                />
                                                <Typography variant="h6" component="h2" gutterBottom>
                                                    {company.name}
                                                </Typography>
                                            </Box>
                                            {isAdmin && (
                                                <Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDialog(company);
                                                        }}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(company.id);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            <strong>Phone:</strong> {company.phone || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {company.description || 'No description available'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>

                {/* Company Creation/Edit Dialog */}
                <Dialog 
                    open={openDialog} 
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            {isEditing ? (isAdmin ? 'Edit Company' : 'View Company') : 'Add New Company'}
                            <IconButton onClick={handleCloseDialog} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Avatar
                                    src={logoPreview || (selectedCompany?.logo ? getLogoUrl(selectedCompany.logo) : null)}
                                    alt="Company Logo"
                                    sx={{ width: 100, height: 100, mb: 2 }}
                                    onError={(e) => {
                                        e.target.src = ''; // Clear the src on error
                                        e.target.style.display = 'none'; // Hide the avatar if image fails to load
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ mb: 2 }}
                                >
                                    Upload Logo
                                    <input
                                        type="file"
                                        hidden
                                        id="company-logo-upload"
                                        name="logo"
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                    />
                                </Button>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Company Name"
                                        name="name"
                                        id="company-name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        id="company-phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        required
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        id="company-email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        id="company-description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={3}
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        {(!isEditing || isAdmin) && (
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={!formData.name || uploading || (isEditing && !isAdmin)}
                            >
                                {uploading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    isEditing ? 'Update' : 'Create'
                                )}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
}

export default Companies;